import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { ChevronRight, ChevronLeft, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface QuestionnaireData {
  primaryGoal: string;
  experienceLevel: string;
  learningStyle: string;
  specificChallenges: string[];
  preferredGenres: string[];
  daw: string;
  timeCommitment: string;
  regenerate?: boolean;
}

interface MentorMatchQuestionnaireProps {
  onComplete: (recommendations: any[]) => void;
  onCancel: () => void;
  isRetake?: boolean;
}

export const MentorMatchQuestionnaire: React.FC<MentorMatchQuestionnaireProps> = ({ 
  onComplete, 
  onCancel,
  isRetake = false,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
const [formData, setFormData] = useState<QuestionnaireData>({
  primaryGoal: "",
  experienceLevel: "",
  learningStyle: "",
  specificChallenges: [],
  preferredGenres: [],
  daw: "",
  timeCommitment: "",
});

  const totalSteps = 7;
  const progress = (currentStep / totalSteps) * 100;

  const handleNext = () => {
    // Validate current step
const validations = [
  formData.primaryGoal,
  formData.experienceLevel,
  formData.learningStyle,
  formData.specificChallenges.length > 0,
  formData.daw,
  formData.preferredGenres.length > 0,
  formData.timeCommitment,
];

    if (!validations[currentStep - 1]) {
      toast({
        title: "Please complete this step",
        description: "Select at least one option before continuing.",
        variant: "destructive",
      });
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData.timeCommitment) {
      toast({
        title: "Please complete all steps",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

try {
      const { data, error } = await supabase.functions.invoke('match-mentors', {
        body: { questionnaireData: { ...formData, regenerate: isRetake } }
      });

      if (error) throw error;

      if (data?.recommendations && data.recommendations.length > 0) {
        onComplete(data.recommendations);
        toast({
          title: "Perfect Match Found! 🎯",
          description: `We've found ${data.recommendations.length} ideal mentors for you.`,
        });
      } else {
        toast({
          title: "No matches found",
          description: "Please try adjusting your preferences or browse all mentors.",
          variant: "destructive",
        });
        onCancel();
      }
    } catch (error: any) {
      console.error('Error matching mentors:', error);
      
      // Check if this is the "already completed" error
      const errorMessage = error?.message || '';
      if (errorMessage.includes('already completed') || errorMessage.includes('only take it once')) {
        toast({
          title: "Questionnaire Already Completed",
          description: "You've already completed the mentor matching questionnaire. Check your recommendations above.",
          variant: "destructive",
        });
        onCancel();
      } else {
        toast({
          title: "Matching Error",
          description: "Unable to find mentor matches. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleChallenge = (challenge: string) => {
    setFormData(prev => ({
      ...prev,
      specificChallenges: prev.specificChallenges.includes(challenge)
        ? prev.specificChallenges.filter(c => c !== challenge)
        : [...prev.specificChallenges, challenge]
    }));
  };

  const toggleGenre = (genre: string) => {
    setFormData(prev => ({
      ...prev,
      preferredGenres: prev.preferredGenres.includes(genre)
        ? prev.preferredGenres.filter(g => g !== genre)
        : [...prev.preferredGenres, genre]
    }));
  };

  return (
    <Card className="obsidian-card border-primary/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          Find Your Perfect Mentor
        </CardTitle>
        <CardDescription>
          Answer a few questions to get personalized mentor recommendations
        </CardDescription>
        <Progress value={progress} className="mt-4" />
        <p className="text-xs text-muted-foreground mt-2">
          Step {currentStep} of {totalSteps}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Step 1: Primary Goal */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <Label className="text-base font-semibold">What's your primary goal?</Label>
            <RadioGroup value={formData.primaryGoal} onValueChange={(value) => setFormData({ ...formData, primaryGoal: value })}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="master-daw" id="master-daw" />
                <Label htmlFor="master-daw" className="font-normal cursor-pointer">Master a specific DAW (Ableton, Logic, FL Studio, etc.)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="mixing-mastering" id="mixing-mastering" />
                <Label htmlFor="mixing-mastering" className="font-normal cursor-pointer">Improve mixing & mastering skills</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="music-theory" id="music-theory" />
                <Label htmlFor="music-theory" className="font-normal cursor-pointer">Learn music theory & composition</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="marketing" id="marketing" />
                <Label htmlFor="marketing" className="font-normal cursor-pointer">Develop marketing & release strategy</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="project-feedback" id="project-feedback" />
                <Label htmlFor="project-feedback" className="font-normal cursor-pointer">Get feedback on existing projects</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="sound-design" id="sound-design" />
                <Label htmlFor="sound-design" className="font-normal cursor-pointer">Advanced sound design techniques</Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Step 2: Experience Level */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <Label className="text-base font-semibold">What's your experience level?</Label>
            <RadioGroup value={formData.experienceLevel} onValueChange={(value) => setFormData({ ...formData, experienceLevel: value })}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="beginner" id="beginner" />
                <Label htmlFor="beginner" className="font-normal cursor-pointer">Complete Beginner</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="some-experience" id="some-experience" />
                <Label htmlFor="some-experience" className="font-normal cursor-pointer">Some Experience (1-2 years)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="intermediate" id="intermediate" />
                <Label htmlFor="intermediate" className="font-normal cursor-pointer">Intermediate (3-5 years)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="advanced" id="advanced" />
                <Label htmlFor="advanced" className="font-normal cursor-pointer">Advanced (5+ years)</Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Step 3: Learning Style */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <Label className="text-base font-semibold">What's your preferred learning style?</Label>
            <RadioGroup value={formData.learningStyle} onValueChange={(value) => setFormData({ ...formData, learningStyle: value })}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="hands-on" id="hands-on" />
                <Label htmlFor="hands-on" className="font-normal cursor-pointer">Hands-on project-based learning</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="structured" id="structured" />
                <Label htmlFor="structured" className="font-normal cursor-pointer">Step-by-step structured guidance</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="creative" id="creative" />
                <Label htmlFor="creative" className="font-normal cursor-pointer">Creative exploration & experimentation</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="technical" id="technical" />
                <Label htmlFor="technical" className="font-normal cursor-pointer">Technical deep-dives</Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Step 4: Specific Challenges */}
        {currentStep === 4 && (
          <div className="space-y-4">
            <Label className="text-base font-semibold">What specific challenges do you face? (Select all that apply)</Label>
            <div className="space-y-3">
              {[
                'Sound design',
                'Arrangement & structure',
                'Mixing techniques',
                'Mastering',
                'Music theory',
                'Finding my unique sound',
                'Building audience/marketing',
                'Technical setup & workflow'
              ].map((challenge) => (
                <div key={challenge} className="flex items-center space-x-2">
                  <Checkbox
                    id={challenge}
                    checked={formData.specificChallenges.includes(challenge)}
                    onCheckedChange={() => toggleChallenge(challenge)}
                  />
                  <Label htmlFor={challenge} className="font-normal cursor-pointer">{challenge}</Label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 5: DAW */}
        {currentStep === 5 && (
          <div className="space-y-4">
            <Label className="text-base font-semibold">What DAW do you use?</Label>
            <RadioGroup value={formData.daw} onValueChange={(value) => setFormData({ ...formData, daw: value })}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Ableton" id="daw-ableton" />
                <Label htmlFor="daw-ableton" className="font-normal cursor-pointer">Ableton</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Logic" id="daw-logic" />
                <Label htmlFor="daw-logic" className="font-normal cursor-pointer">Logic</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Pro Tools" id="daw-pro-tools" />
                <Label htmlFor="daw-pro-tools" className="font-normal cursor-pointer">Pro Tools</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="FL Studio" id="daw-fl-studio" />
                <Label htmlFor="daw-fl-studio" className="font-normal cursor-pointer">FL Studio</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Other" id="daw-other" />
                <Label htmlFor="daw-other" className="font-normal cursor-pointer">Other</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="None" id="daw-none" />
                <Label htmlFor="daw-none" className="font-normal cursor-pointer">I don't currently use a DAW</Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Step 6: Preferred Genres */}
        {currentStep === 6 && (
          <div className="space-y-4">
            <Label className="text-base font-semibold">What genres are you interested in? (Select all that apply)</Label>
            <div className="space-y-3">
              {[
                'EDM',
                'Rock and Metal',
                'Rap and Hip Hop',
                'Pop',
                'Indie',
                'Other'
              ].map((genre) => (
                <div key={genre} className="flex items-center space-x-2">
                  <Checkbox
                    id={genre}
                    checked={formData.preferredGenres.includes(genre)}
                    onCheckedChange={() => toggleGenre(genre)}
                  />
                  <Label htmlFor={genre} className="font-normal cursor-pointer">{genre}</Label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 7: Time Commitment */}
        {currentStep === 7 && (
          <div className="space-y-4">
            <Label className="text-base font-semibold">What's your time commitment?</Label>
            <RadioGroup value={formData.timeCommitment} onValueChange={(value) => setFormData({ ...formData, timeCommitment: value })}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="quick-wins" id="quick-wins" />
                <Label htmlFor="quick-wins" className="font-normal cursor-pointer">Looking for quick wins (1-2 sessions)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="medium-term" id="medium-term" />
                <Label htmlFor="medium-term" className="font-normal cursor-pointer">Medium-term project (3-5 sessions)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="long-term" id="long-term" />
                <Label htmlFor="long-term" className="font-normal cursor-pointer">Long-term mentorship (6+ sessions)</Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? onCancel : handleBack}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </Button>
          
          {currentStep < totalSteps ? (
            <Button onClick={handleNext}>
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Finding Matches...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Find My Mentors
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
